import { isVue2, h as h$1, defineComponent } from 'vue-demi';
import getStroke from 'perfect-freehand';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const adaptOnsV3 = (ons) => {
    if (!ons)
        return null;
    return Object.entries(ons).reduce((ret, [key, handler]) => {
        key = key.charAt(0).toUpperCase() + key.slice(1);
        key = `on${key}`;
        return Object.assign(Object.assign({}, ret), { [key]: handler });
    }, {});
};
const h = (type, options = {}, chidren) => {
    if (isVue2)
        return h$1(type, options, chidren);
    const { props, domProps, on } = options, extraOptions = __rest(options, ["props", "domProps", "on"]);
    let ons = adaptOnsV3(on);
    const params = Object.assign(Object.assign(Object.assign(Object.assign({}, extraOptions), props), domProps), ons);
    return h$1(type, params, chidren);
};

function getSvgPathFromStroke(stroke) {
    if (!stroke.length)
        return "";
    const d = stroke.reduce((acc, [x0, y0], i, arr) => {
        const [x1, y1] = arr[(i + 1) % arr.length];
        acc.push(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
        return acc;
    }, ["M", ...stroke[0], "Q"]);
    d.push("Z");
    return d.join(" ");
}

const DEFAULT_BACKGROUND_COLOR = 'rgba(0,0,0,0)';
const DEFAULT_PEN_COLOR = '#000';
const DEFAULT_WIDTH = '100%';
const DEFAULT_HEIGHT = '100%';
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

var VPerfectSignature = defineComponent({
    data: () => ({
        allInputPoints: [],
        currentInputPoints: null,
        isDrawing: false,
        cachedImages: []
    }),
    emits: ['onBegin', 'onEnd'],
    props: {
        width: {
            type: String,
            required: false,
            default: DEFAULT_WIDTH
        },
        height: {
            type: String,
            required: false,
            default: DEFAULT_HEIGHT
        },
        backgroundColor: {
            type: String,
            required: false,
            default: DEFAULT_BACKGROUND_COLOR
        },
        penColor: {
            type: String,
            required: false,
            default: DEFAULT_PEN_COLOR
        },
        strokeOptions: {
            type: Object,
            required: false,
            default: () => ({})
        }
    },
    methods: {
        handlePointerDown(e) {
            e.preventDefault();
            const canvas = e.composedPath()[0];
            const rect = canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            this.currentInputPoints = [[x, y, e.pressure]];
            this.isDrawing = true;
            this.$emit('onBegin', e);
        },
        handlePointerMove(e) {
            var _a;
            if (!this.isDrawing)
                return;
            if (e.buttons === 1) {
                e.preventDefault();
                const canvas = e.composedPath()[0];
                const rect = canvas.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                this.currentInputPoints = [...(_a = this.currentInputPoints) !== null && _a !== void 0 ? _a : [], [x, y, e.pressure]];
            }
        },
        handlePointerUp(e) {
            e.preventDefault();
            this.isDrawing = false;
            if (!this.currentInputPoints)
                return;
            this.allInputPoints = [...this.allInputPoints, this.currentInputPoints];
            this.currentInputPoints = null;
            this.$emit('onEnd', e);
        },
        handlePointerEnter(e) {
            if (e.buttons === 1) {
                this.handlePointerDown(e);
            }
        },
        handlePointerLeave(e) {
            if (!this.isDrawing)
                return;
            this.handlePointerUp(e);
        },
        isEmpty() {
            return !this.allInputPoints.length && !this.cachedImages.length;
        },
        clear() {
            this.cachedImages = [];
            this.allInputPoints = [];
            this.currentInputPoints = null;
        },
        fromData(data) {
            this.allInputPoints = [...this.allInputPoints, ...data];
        },
        toData() {
            return this.allInputPoints;
        },
        fromDataURL(data) {
            return new Promise((resolve, reject) => {
                const image = new Image();
                image.onload = () => {
                    const canvas = this.getCanvasElement();
                    const ctx = canvas.getContext('2d');
                    ctx === null || ctx === void 0 ? void 0 : ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
                    this.cachedImages.push(image);
                    resolve(true);
                };
                image.onerror = () => {
                    reject('Incorrect data uri provided');
                };
                image.crossOrigin = 'anonymous';
                image.src = data;
            });
        },
        toDataURL(type) {
            if (type && !IMAGE_TYPES.includes(type)) {
                throw new Error(`Incorrect image type. Must be one of ${IMAGE_TYPES.join(', ')}.`);
            }
            if (this.isEmpty())
                return;
            const canvas = this.getCanvasElement();
            return canvas.toDataURL(type !== null && type !== void 0 ? type : 'image/png');
        },
        getCanvasElement() {
            return this.$refs.signaturePad;
        },
        setBackgroundAndPenColor() {
            const canvas = this.getCanvasElement();
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = this.backgroundColor;
            ctx === null || ctx === void 0 ? void 0 : ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = this.penColor;
        },
        resizeCanvas(clearCanvas = true) {
            const canvas = this.getCanvasElement();
            const rect = canvas.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            const ctx = canvas.getContext('2d');
            ctx === null || ctx === void 0 ? void 0 : ctx.scale(dpr, dpr);
            canvas.style.width = rect.width + 'px';
            canvas.style.height = rect.height + 'px';
            if (clearCanvas) {
                this.clear();
            }
            this.setBackgroundAndPenColor();
        },
        inputPointsHandler() {
            const canvas = this.getCanvasElement();
            const ctx = canvas.getContext('2d');
            ctx === null || ctx === void 0 ? void 0 : ctx.clearRect(0, 0, canvas.width, canvas.height);
            this.cachedImages.forEach((image) => ctx === null || ctx === void 0 ? void 0 : ctx.drawImage(image, 0, 0, canvas.width, canvas.height));
            this.setBackgroundAndPenColor();
            this.allInputPoints.forEach((point) => {
                const pathData = getSvgPathFromStroke(getStroke(point, this.strokeOptions));
                const myPath = new Path2D(pathData);
                ctx === null || ctx === void 0 ? void 0 : ctx.fill(myPath);
            });
            if (!this.currentInputPoints)
                return;
            const pathData = getSvgPathFromStroke(getStroke(this.currentInputPoints, this.strokeOptions));
            const myPath = new Path2D(pathData);
            ctx === null || ctx === void 0 ? void 0 : ctx.fill(myPath);
        }
    },
    mounted() {
        this.resizeCanvas();
    },
    watch: {
        backgroundColor() {
            this.setBackgroundAndPenColor();
        },
        penColor(color) {
            const canvas = this.getCanvasElement();
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = color;
        },
        allInputPoints: {
            deep: true,
            handler() {
                this.inputPointsHandler();
            }
        },
        currentInputPoints: {
            deep: true,
            handler() {
                this.inputPointsHandler();
            }
        }
    },
    render() {
        const { width, height } = this;
        const handlers = {
            pointerdown: this.handlePointerDown,
            pointerup: this.handlePointerUp,
            pointermove: this.handlePointerMove,
            pointerenter: this.handlePointerEnter,
            pointerleave: this.handlePointerLeave
        };
        return h('canvas', {
            ref: 'signaturePad',
            style: {
                height,
                width,
                touchAction: 'none'
            },
            on: handlers
        });
    }
});

export default VPerfectSignature;
//# sourceMappingURL=v-perfect-signature.esm.js.map
